import React from 'react';
import { Box, Code, Tooltip } from '@radix-ui/themes';
import { GraphQLTable, Column } from '../../components/GraphQLTable';
import HashShortener from '../../components/HashShortener';
import { AddressDisplay } from '../../components/AddressDisplay';
import BlockNumberDisplay from '../../components/BlockNumberDisplay';
import ViewButton from '../../components/ViewButton';
import {
  GetDecodedEventsDocument,
  GetDecodedEventsQuery,
  GetDecodedEventsQueryVariables,
} from '../../graphql/queries/GetDecodedEvents.generated';
import { FilterOperation } from '../../types';

type DecodedEventNode =
  GetDecodedEventsQuery['decodedEvents']['edges'][0]['node'];

const EventList: React.FC = () => {
  // Format decoded data for tooltip display
  const formatDecodedData = (data: any): string => {
    if (!data || typeof data !== 'object') return 'No data';

    try {
      return Object.entries(data)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');
    } catch {
      return 'Invalid data';
    }
  };

  const columns: Column<DecodedEventNode>[] = [
    {
      header: 'Tx Hash',
      render: ({ row }) =>
        row.log?.transactionHash ? (
          <HashShortener
            hash={row.log.transactionHash}
            link={`/transactions/${row.log.transactionHash}`}
          />
        ) : (
          'N/A'
        ),
      filterable: true,
      filterField: 'log.transactionHash',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search transaction hash...',
      },
    },
    {
      header: 'Contract',
      render: ({ row }) =>
        row.log?.address ? <AddressDisplay address={row.log.address} /> : 'N/A',
      filterable: true,
      filterField: 'log.address',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search contract address...',
      },
    },
    {
      header: 'Event Name',
      accessor: 'eventName',
      filterable: true,
      filterField: 'eventName',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search event name...',
      },
    },
    {
      header: 'Signature',
      accessor: 'eventSignature',
      render: ({ value, row }) => (
        <Tooltip content={formatDecodedData(row.decodedData)}>
          <Code variant="ghost" style={{ fontSize: '0.9em' }}>
            {value}
          </Code>
        </Tooltip>
      ),
      filterable: true,
      filterField: 'eventSignature',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search event signature...',
      },
    },
    {
      header: 'Block',
      render: ({ row }) =>
        row.log?.blockNumber ? (
          <BlockNumberDisplay
            blockNumber={row.log.blockNumber}
            link={`/blocks/${row.log.blockNumber}`}
          />
        ) : (
          'N/A'
        ),
      filterable: true,
      filterField: 'log.blockNumber',
      filterConfig: {
        type: 'number',
        operation: FilterOperation.Eq,
        placeholder: 'Block number...',
      },
    },
    {
      header: 'View',
      render: ({ row }) => <ViewButton to={`/events/${row.id}`} />,
    },
  ];

  return (
    <Box>
      <GraphQLTable<
        GetDecodedEventsQuery,
        GetDecodedEventsQueryVariables,
        'decodedEvents',
        DecodedEventNode
      >
        query={GetDecodedEventsDocument}
        columns={columns}
        connectionPath="decodedEvents"
        loadingMessage="Fetching decoded events..."
        errorMessage="Failed to fetch decoded events."
        noDataMessage="No decoded events available."
      />
    </Box>
  );
};

export default EventList;
